import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Link
} from '@mui/material';
import { getSeasonName } from '../utils/dateUtils.js';
import CreditChip from './CreditChip.js';
import { getPeriodChipColor, getSemesterChipColor } from '../utils/colorUtils.js';
import { CREDIT_LIMITS } from '../utils/constants.js';



/**
 * Helper function to generate smart border styling for multi-period courses
 */
const getSmartBorderStyling = (course) => {
  const strongColor = course.isSelected ? '#1976d2' : '#ff9800'; // Strong blue or orange
  const borderStyle = course.isSelected ? 'solid' : 'dashed'; // Solid for selected, dashed for backup
  
  if (!course.is_split_course) {
    // Single period course - bold border all around (solid for selected, dashed for backup)
    return {
      border: `2px ${borderStyle} ${strongColor}`
    };
  }
  
  const totalParts = course.period_credits?.length || 1;
  const currentPart = course.period_index;
  
  // Multi-period course smart borders
  const isFirst = currentPart === 0;
  const isLast = currentPart === totalParts - 1;
  const isMiddle = !isFirst && !isLast;
  
  if (isFirst && totalParts > 1) {
    // First part: borders except right side borderless (flows to next period)
    return {
      borderTop: `2px ${borderStyle} ${strongColor}`,
      borderBottom: `2px ${borderStyle} ${strongColor}`,
      borderLeft: `2px ${borderStyle} ${strongColor}`,
      borderRight: 'none'
    };
  } else if (isLast && totalParts > 1) {
    // Last part: borders except left side borderless (flows from previous period)
    return {
      borderTop: `2px ${borderStyle} ${strongColor}`,
      borderBottom: `2px ${borderStyle} ${strongColor}`,
      borderLeft: 'none',
      borderRight: `2px ${borderStyle} ${strongColor}`
    };
  } else if (isMiddle) {
    // Middle part: borders except left and right sides borderless (flows both ways)
    return {
      borderTop: `2px ${borderStyle} ${strongColor}`,
      borderBottom: `2px ${borderStyle} ${strongColor}`,
      borderLeft: 'none',
      borderRight: 'none'
    };
  }
  
  // Fallback to bold border
  return {
    border: `2px ${borderStyle} ${strongColor}`
  };
};

/**
 * Timetable view showing courses in a calendar/timeline format
 */
const TimetableView = ({ groupedCourses, selectedIds, backupIds }) => {
  // Organize courses by year, semester, and period
  const timetableData = {};
  
  Object.entries(groupedCourses).forEach(([groupKey, group]) => {
    const { year, semester, period } = group;
    
    if (!timetableData[year]) {
      timetableData[year] = {};
    }
    if (!timetableData[year][semester]) {
      timetableData[year][semester] = {
        1: [],
        2: []
      };
    }
    
    // Get courses for this period that are selected or backup
    const relevantCourses = group.courses.filter(course => {
      const courseId = course.is_split_course
        ? `${course.semester_instance}_p${course.actual_period}`
        : course.semester_instance;
      return selectedIds.has(courseId) || backupIds.has(courseId);
    }).map(course => {
      const courseId = course.is_split_course
        ? `${course.semester_instance}_p${course.actual_period}`
        : course.semester_instance;
      return {
        ...course,
        isSelected: selectedIds.has(courseId),
        isBackup: backupIds.has(courseId)
      };
    }).sort((a, b) => {
      // Sort selected courses first, then backup courses
      if (a.isSelected && !b.isSelected) return -1;
      if (!a.isSelected && b.isSelected) return 1;
      return 0; // Keep original order for courses of the same type
    });
    
    timetableData[year][semester][period] = relevantCourses;
  });

  // Sort years
  const sortedYears = Object.keys(timetableData).sort((a, b) => parseInt(a) - parseInt(b));

  if (sortedYears.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography color="text.secondary">
          No courses selected yet. Use the toggle above to return to the course selection interface.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      {sortedYears.map(year => {
        const yearData = timetableData[year];
        const semesters = Object.keys(yearData).sort((a, b) => parseInt(a) - parseInt(b));
        
        return (
          <Box key={year} sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
              {year}
            </Typography>
            
            {semesters.map(semester => {
              const semesterData = yearData[semester];
              const period1Courses = semesterData[1] || [];
              const period2Courses = semesterData[2] || [];
              
              // Calculate period credits (only count selected courses, not backup)
              const period1Credits = period1Courses.reduce((sum, course) => {
                return course.isSelected ? sum + course.credits : sum;
              }, 0);
              const period2Credits = period2Courses.reduce((sum, course) => {
                return course.isSelected ? sum + course.credits : sum;
              }, 0);
              
              // Skip if no courses in either period
              if (period1Courses.length === 0 && period2Courses.length === 0) {
                return null;
              }
              
              // Calculate total semester credits (only selected courses)
              const semesterCredits = period1Credits + period2Credits;
              
              return (
                <Box key={`${year}-${semester}`} sx={{ mb: 3, width: '100%' }}>
                  <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {getSeasonName(parseInt(semester), parseInt(year))}
                    </Typography>
                    <CreditChip
                      credits={semesterCredits}
                      maxCredits={CREDIT_LIMITS.SEMESTER_OPTIMAL}
                      type="semester"
                      getColorFn={getSemesterChipColor}
                      variant={semesterCredits > 0 ? 'filled' : 'outlined'}
                      size="medium"
                    />
                  </Box>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      width: '100%',
                      flexDirection: { xs: 'column', md: 'row' }
                    }}
                  >
                    {/* Period 1 */}
                    <Box sx={{ flex: 1, display: 'flex' }}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          width: '100%',
                          minHeight: '150px',
                          display: 'flex',
                          flexDirection: 'column',
                          backgroundColor: period1Courses.length > 0 ? 'rgba(25, 118, 210, 0.02)' : 'rgba(0, 0, 0, 0.01)'
                        }}
                      >
                        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 600,
                                color: 'primary.main'
                              }}
                            >
                              1st Period
                            </Typography>
                                                        <CreditChip 
                              credits={period1Credits}
                              maxCredits={15}
                              type="period"
                              getColorFn={getPeriodChipColor}
                              size="small"
                            />
                          </Box>
                          
                          <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {period1Courses.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No courses scheduled
                              </Typography>
                            ) : (
                              period1Courses.map((course, index) => (
                              <Card 
                                key={index}
                                variant="outlined"
                                sx={{ 
                                  mb: 1,
                                  backgroundColor: course.isSelected 
                                    ? 'rgba(25, 118, 210, 0.08)' 
                                    : 'rgba(255, 193, 7, 0.08)',
                                  ...getSmartBorderStyling(course)
                                }}
                              >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                    {course.course_name}
                                  </Typography>
                                  
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Link
                                      href={`https://www.uu.se/en/study/course?query=${course.course_code}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      color="primary"
                                      underline="hover"
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {course.course_code}
                                    </Link>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {course.credits} credits
                                    </Typography>
                                  </Box>
                                  
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    {course.is_split_course && (
                                      <Typography variant="caption" color="textSecondary">
                                        Part {course.period_index + 1} of {course.period_credits?.length || 0}
                                      </Typography>
                                    )}
                                    <Typography 
                                      variant="caption" 
                                      sx={{
                                        color: course.isSelected ? 'success.main' : 'warning.main',
                                        fontWeight: 500,
                                        ml: 'auto'
                                      }}
                                    >
                                      {course.isSelected ? 'Selected' : 'Backup'}
                                    </Typography>
                                  </Box>
                                  
                                  {course.notes && (
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                      {course.notes}
                                    </Typography>
                                  )}
                                </CardContent>
                              </Card>
                            ))
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                    
                    {/* Period 2 */}
                    <Box sx={{ flex: 1, display: 'flex' }}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          width: '100%',
                          minHeight: '150px',
                          display: 'flex',
                          flexDirection: 'column',
                          backgroundColor: period2Courses.length > 0 ? 'rgba(25, 118, 210, 0.02)' : 'rgba(0, 0, 0, 0.01)'
                        }}
                      >
                        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 600,
                                color: 'primary.main'
                              }}
                            >
                              2nd Period
                            </Typography>
                                                        <CreditChip 
                              credits={period2Credits}
                              maxCredits={15}
                              type="period"
                              getColorFn={getPeriodChipColor}
                              size="small"
                            />
                          </Box>
                          
                          <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {period2Courses.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No courses scheduled
                              </Typography>
                            ) : (
                              period2Courses.map((course, index) => (
                              <Card 
                                key={index}
                                variant="outlined"
                                sx={{ 
                                  mb: 1,
                                  backgroundColor: course.isSelected 
                                    ? 'rgba(25, 118, 210, 0.08)' 
                                    : 'rgba(255, 193, 7, 0.08)',
                                  ...getSmartBorderStyling(course)
                                }}
                              >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                    {course.course_name}
                                  </Typography>
                                  
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Link
                                      href={`https://www.uu.se/en/study/course?query=${course.course_code}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      color="primary"
                                      underline="hover"
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {course.course_code}
                                    </Link>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {course.credits} credits
                                    </Typography>
                                  </Box>
                                  
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    {course.is_split_course && (
                                      <Typography variant="caption" color="textSecondary">
                                        Part {course.period_index + 1} of {course.period_credits?.length || 0}
                                      </Typography>
                                    )}
                                    <Typography 
                                      variant="caption" 
                                      sx={{
                                        color: course.isSelected ? 'success.main' : 'warning.main',
                                        fontWeight: 500,
                                        ml: 'auto'
                                      }}
                                    >
                                      {course.isSelected ? 'Selected' : 'Backup'}
                                    </Typography>
                                  </Box>
                                  
                                  {course.notes && (
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                      {course.notes}
                                    </Typography>
                                  )}
                                </CardContent>
                              </Card>
                            ))
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Paper>
  );
};

export default TimetableView; 